import { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";
import { Dumbbell } from "lucide-react";
import { PushService, registerPushToken } from "../services/push.service";
import { BiometricService } from "../services/biometric.service";

import { wsUrl } from "../lib/wsUrl";

const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const notifListenersRef = useRef([]);

  // Build the WS URL from the API origin, not the page origin. In production
  // Traefik serves the app and the API from one host, so both are identical;
  // anywhere they are split (CI, a local dev server on another port) deriving
  // it from window.location points the socket at the static file server, which
  // answers the upgrade with index.html and never connects.
  const getWsUrl = useCallback(
    (authToken) => wsUrl("/ws/notifications", authToken),
    []
  );

  const connectWs = useCallback((authToken) => {
    if (wsRef.current) {
      try { wsRef.current.close(); } catch {}
    }
    try {
      const ws = new WebSocket(getWsUrl(authToken));
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.event === "notification" && msg.data) {
            setNotifications(prev => [msg.data, ...prev].slice(0, 50));
            setUnreadCount(prev => prev + 1);
            // Notify listeners (dashboards can show toasts)
            notifListenersRef.current.forEach(fn => fn(msg.data));
          }
        } catch {}
      };

      ws.onclose = () => {
        // Reconnect after 5s if still authenticated
        setTimeout(() => {
          const t = localStorage.getItem('token');
          if (t) connectWs(t);
        }, 5000);
      };

      // Send ping every 30s to keep alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send("ping");
      }, 30000);

      ws.addEventListener("close", () => clearInterval(pingInterval));
    } catch {}
  }, [getWsUrl]);

  const disconnectWs = useCallback(() => {
    if (wsRef.current) {
      try { wsRef.current.close(); } catch {}
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    const t = localStorage.getItem('token');
    const u = localStorage.getItem('user');
    if (t && u) {
      setToken(t);
      setUser(JSON.parse(u));
      connectWs(t);
      initPush(t);
    }
    setLoading(false);
    return () => disconnectWs();
  }, []);

  const pushServiceRef = useRef(null);

  const initPush = useCallback((authToken) => {
    if (pushServiceRef.current) return;
    const push = new PushService({
      onToken: (pushToken, platform) => {
        registerPushToken(pushToken, platform, authToken);
      },
      onNotification: (payload, notification) => {
        // Foreground push — show via notification listeners
        notifListenersRef.current.forEach(fn => fn({
          id: Date.now().toString(),
          type: payload.type || 'info',
          title: notification.title || '',
          message: notification.body || '',
          read: false,
          created_at: new Date().toISOString(),
        }));
      },
      onTap: () => {
        // Tapped notification — could navigate, but our app uses tabs
      },
    });
    pushServiceRef.current = push;
    push.init();
  }, []);

  const login = (t, u) => {
    setToken(t); setUser(u);
    localStorage.setItem('token', t);
    localStorage.setItem('user', JSON.stringify(u));
    connectWs(t);
    initPush(t);
    // Offer biometric enrollment after successful password login
    BiometricService.isAvailable().then(available => {
      if (available) {
        BiometricService.hasEnrolledToken().then(enrolled => {
          if (!enrolled) BiometricService.enrollToken(t);
        });
      }
    });
  };

  const updateUser = (patch) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...(patch || {}) };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  const logout = () => {
    disconnectWs();
    if (pushServiceRef.current) { pushServiceRef.current.unregister(); pushServiceRef.current = null; }
    BiometricService.clearEnrolledToken();
    setToken(null); setUser(null);
    setNotifications([]);
    setUnreadCount(0);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const markNotificationsRead = () => setUnreadCount(0);

  const onNotification = useCallback((fn) => {
    notifListenersRef.current.push(fn);
    return () => {
      notifListenersRef.current = notifListenersRef.current.filter(f => f !== fn);
    };
  }, []);

  if (loading) return <div className="loading"><Dumbbell className="spin" size={32} /></div>;

  return (
    <AuthContext.Provider value={{
      user, token, isAuthenticated: !!token,
      login, logout, updateUser,
      notifications, unreadCount, markNotificationsRead, onNotification
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider, useAuth };
